import { startTransition, useCallback, useEffect, useRef, useState } from "react";
import {
  ROLE_CONFIG,
  getCurrentEmployee,
  getErrorMessage,
  getMasters,
  getSession,
  isSupabaseConfigured,
  signOut,
  supabase,
} from "./api";
import { S } from "./styles";
import { ensureAllowedScreen, roleBadgeStyle } from "./utils";
import { GlobalStyles, FullPageLoader, Toast } from "./ui";
import { SetupScreen, LoginScreen } from "./screens/AuthScreens";
import { EntryScreen } from "./screens/EntryScreen";
import { HistoryScreen } from "./screens/HistoryScreen";
import { DashboardScreen } from "./screens/DashboardScreen";
import { AnalysisScreen } from "./screens/AnalysisScreen";
import { AdminScreen } from "./screens/AdminScreen";
import { QuickAddScreen } from "./screens/QuickAddScreen";

const EMPTY_MASTERS = {
  supervisors: [],
  machines: [],
  parts: [],
  operators: [],
  downtimeReasons: [],
  rejectionReasons: [],
};

const EMPTY_ADMIN_VIEW_STATE = {
  tab: "masters",
  masterKey: "machines",
  exportKey: "employees",
  masterDrafts: {
    machines: { name: "", target: "" },
    parts: { name: "", target: "" },
    operators: { name: "", target: "" },
    downtimeReasons: { name: "", target: "" },
    rejectionReasons: { name: "", target: "" },
  },
  editingPart: null,
  exportFilters: {
    startDate: "",
    endDate: "",
    machineId: "",
    supervisorId: "",
    partId: "",
  },
  employeeForm: {
    employeeId: "",
    fullName: "",
    role: "supervisor",
    pin: "1234",
  },
  recipientForm: {
    full_name: "",
    email: "",
    active: true,
    receives_daily: true,
    receives_monthly: true,
  },
  pinDrafts: {},
};

export default function App() {
  const [user, setUser] = useState(null);
  const [masters, setMasters] = useState(EMPTY_MASTERS);
  const [booting, setBooting] = useState(true);
  const [screen, setScreen] = useState("login");
  const [editingEntry, setEditingEntry] = useState(null);
  const [adminViewState, setAdminViewState] = useState(EMPTY_ADMIN_VIEW_STATE);
  const [entryDrafts, setEntryDrafts] = useState({});
  const [toast, setToast] = useState(null);
  const [sessionKey, setSessionKey] = useState(0);
  const isCompactLayout = useCompactLayout();

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);

  const hydrateSignedInState = useCallback(async (authUserId) => {
    setBooting(true);
    try {
      const [employee, masterData] = await withTimeout(
        Promise.all([getCurrentEmployee(authUserId), getMasters()]),
        12000,
        "Loading timed out. Please refresh the page.",
      );

      if (!employee?.active) {
        await signOut();
        throw new Error("Your account is inactive. Please contact the admin.");
      }

      startTransition(() => {
        setUser(employee);
        setMasters(masterData || EMPTY_MASTERS);
        setEditingEntry(null);
        setAdminViewState(EMPTY_ADMIN_VIEW_STATE);
        setEntryDrafts({});
        setSessionKey((prev) => prev + 1);
        setScreen((current) => ensureAllowedScreen(current, employee.role));
      });
    } catch (error) {
      startTransition(() => {
        setUser(null);
        setMasters(EMPTY_MASTERS);
        setEditingEntry(null);
        setAdminViewState(EMPTY_ADMIN_VIEW_STATE);
        setEntryDrafts({});
        setScreen("login");
      });

      const message = getErrorMessage(error, "Unable to load your account.");
      if (message !== "Supabase environment variables are missing.") {
        showToast(message, "error");
      }
    } finally {
      setBooting(false);
    }
  }, [showToast]);

  const refreshMasters = useCallback(async () => {
    const masterData = await getMasters();
    setMasters(masterData || EMPTY_MASTERS);
    return masterData;
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setBooting(false);
      return undefined;
    }

    let active = true;

    const boot = async () => {
      try {
      const session = await getSession();
      if (!active) return;
      if (session) {
          await hydrateSignedInState(session.user.id);
        } else {
          setBooting(false);
        }
      } catch (error) {
        if (!active) return;
        setBooting(false);
        showToast(getErrorMessage(error, "Unable to start the app."), "error");
      }
    };

    boot();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      if (session) {
        void hydrateSignedInState(session.user.id);
      } else {
        startTransition(() => {
          setUser(null);
          setMasters(EMPTY_MASTERS);
          setEditingEntry(null);
          setAdminViewState(EMPTY_ADMIN_VIEW_STATE);
          setEntryDrafts({});
          setScreen("login");
          setBooting(false);
        });
      }
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [hydrateSignedInState, showToast]);

  const handleLogout = useCallback(async () => {
    try {
      await signOut();
      showToast("Signed out.");
    } catch (error) {
      showToast(getErrorMessage(error, "Failed to sign out."), "error");
    }
  }, [showToast]);

  const beginEditEntry = useCallback((entry) => {
    setEditingEntry(entry);
    setScreen("entry");
  }, []);

  const handleEntrySaved = useCallback(() => {
    setEditingEntry(null);
    setScreen("history");
  }, []);

  const setEntryDraft = useCallback((draftKey, draft) => {
    if (!draftKey) {
      return;
    }

    setEntryDrafts((current) => {
      if (!draft) {
        if (!(draftKey in current)) {
          return current;
        }

        const next = { ...current };
        delete next[draftKey];
        return next;
      }

      return {
        ...current,
        [draftKey]: draft,
      };
    });
  }, []);

  if (!isSupabaseConfigured) {
    return (
      <>
        <SetupScreen />
        <GlobalStyles />
      </>
    );
  }

  if (booting) {
    return (
      <>
        <FullPageLoader text="Connecting to live production data..." />
        <GlobalStyles />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <LoginScreen showToast={showToast} />
        {toast && <Toast {...toast} onClose={() => setToast(null)} />}
        <GlobalStyles />
      </>
    );
  }

  const nav = [];
  if (user.role === "supervisor" || user.role === "admin") {
    nav.push({ key: "entry", label: "New Entry" }, { key: "history", label: "My Entries" });
  }
  if (user.role === "supervisor") {
    nav.push({ key: "quickAdd", label: "Quick Add" });
  }
  if (user.role === "supervisor" || user.role === "manager" || user.role === "admin") {
    nav.push({ key: "dashboard", label: "Dashboard" }, { key: "analysis", label: "Analysis" });
  }
  if (user.role === "admin") {
    nav.push({ key: "admin", label: "Admin" });
  }

  return (
    <div style={S.app}>
      <AutofillTrap />
      <TopBar
        isCompactLayout={isCompactLayout}
        nav={nav}
        onLogout={handleLogout}
        onSelectScreen={setScreen}
        screen={screen}
        user={user}
      />
      <div style={S.shell}>
        {!isCompactLayout && (
          <div style={S.navBar}>
            {nav.map((item) => (
              <button
                key={item.key}
                style={{
                  ...S.navBtn,
                  ...(screen === item.key ? S.navActive : {}),
                }}
                onClick={() => {
                  setScreen(item.key);
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        <div style={S.content}>
          {screen === "entry" && (
            <EntryScreen
              key={`entry-${user?.id}-${sessionKey}`}
              draftStore={entryDrafts}
              editingEntry={editingEntry}
              masters={masters}
              onCancelEdit={() => {
                setEditingEntry(null);
                setScreen("history");
              }}
              onDraftChange={setEntryDraft}
              onSaved={handleEntrySaved}
              refreshMasters={refreshMasters}
              showToast={showToast}
              user={user}
            />
          )}
          {screen === "history" && (
            <HistoryScreen onEditEntry={beginEditEntry} showToast={showToast} />
          )}
          {screen === "quickAdd" && (
            <QuickAddScreen
              masters={masters}
              refreshMasters={refreshMasters}
              showToast={showToast}
            />
          )}
          {screen === "dashboard" && (
            <DashboardScreen masters={masters} showToast={showToast} />
          )}
          {screen === "analysis" && (
            <AnalysisScreen masters={masters} showToast={showToast} />
          )}
          {user.role === "admin" && (
            <div style={{ display: screen === "admin" ? "block" : "none" }}>
              <AdminScreen
                initialViewState={adminViewState}
                masters={masters}
                onViewStateChange={setAdminViewState}
                refreshMasters={refreshMasters}
                showToast={showToast}
              />
            </div>
          )}
        </div>
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      <GlobalStyles />
    </div>
  );
}

function AutofillTrap() {
  return (
    <div aria-hidden="true" style={autofillTrapStyle}>
      <input autoComplete="username" name="username" tabIndex={-1} type="text" />
      <input
        autoComplete="current-password"
        name="current-password"
        tabIndex={-1}
        type="password"
      />
      <input autoComplete="cc-number" name="cc-number" tabIndex={-1} type="text" />
      <input
        autoComplete="street-address"
        name="street-address"
        tabIndex={-1}
        type="text"
      />
    </div>
  );
}

function withTimeout(promise, timeoutMs, message) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      window.setTimeout(() => reject(new Error(message)), timeoutMs);
    }),
  ]);
}

function TopBar({ user, onLogout, nav, screen, onSelectScreen, isCompactLayout }) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const profileRef = useRef(null);
  const navRef = useRef(null);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }

      if (navRef.current && !navRef.current.contains(event.target)) {
        setNavOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  useEffect(() => {
    if (!isCompactLayout) {
      setNavOpen(false);
    }
  }, [isCompactLayout]);

  return (
    <div
      style={{
        ...S.topBar,
        ...(isCompactLayout
          ? {
              padding: "14px 16px",
              borderRadius: 24,
            }
          : {}),
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
        <div style={S.brandMark}>SP</div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              ...S.cardTitle,
              letterSpacing: "0.06em",
              lineHeight: 1.1,
            }}
          >
            Company Name
          </div>
          <div
            style={{
              ...S.pageBody,
              marginTop: 4,
              lineHeight: 1.35,
              maxWidth: isCompactLayout ? 180 : 280,
            }}
          >
            {isCompactLayout
              ? "Production tracking and reporting"
              : "Production tracking, analytics, and reporting"}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: 12 }}>
        {isCompactLayout && (
          <div ref={navRef} style={{ position: "relative" }}>
            <button
              aria-label="Open app navigation"
              onClick={() => {
                setNavOpen((current) => !current);
                setProfileOpen(false);
              }}
              style={iconButtonStyle}
            >
              <DotsIcon />
            </button>
            {navOpen && (
              <div style={{ ...menuPanelStyle, right: 0, minWidth: 210 }}>
                <div style={menuPanelHeaderStyle}>Open screen</div>
                <div style={menuListStyle}>
                  {nav.map((item) => (
                    <button
                      key={item.key}
                      onClick={() => {
                        onSelectScreen(item.key);
                        setNavOpen(false);
                      }}
                      style={{
                        ...menuItemStyle,
                        ...(screen === item.key ? activeMenuItemStyle : {}),
                      }}
                    >
                      <span>{item.label}</span>
                      {screen === item.key && <span style={menuMetaTextStyle}>Current</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div ref={profileRef} style={{ position: "relative" }}>
          <button
            aria-label="Open profile menu"
            onClick={() => {
              setProfileOpen((current) => !current);
              setNavOpen(false);
            }}
            style={iconButtonStyle}
          >
            <PersonIcon />
          </button>
          {profileOpen && (
            <div style={{ ...menuPanelStyle, right: 0, minWidth: isCompactLayout ? 220 : 250 }}>
              <div style={S.cardTitle}>
                {user.full_name}
              </div>
              <div style={{ ...S.pageBody, marginTop: 4 }}>
                {user.employee_id}
              </div>
              <div style={{ marginTop: 12 }}>
                <span
                  style={{
                    ...S.badge,
                    ...roleBadgeStyle(user.role),
                  }}
                >
                  {ROLE_CONFIG[user.role].label}
                </span>
              </div>
              <button onClick={onLogout} style={{ ...S.primaryBtn, width: "100%", marginTop: 16 }}>
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const autofillTrapStyle = {
  position: "fixed",
  width: 0,
  height: 0,
  opacity: 0,
  overflow: "hidden",
  pointerEvents: "none",
  inset: 0,
};

function useCompactLayout() {
  const getMatch = () =>
    typeof window !== "undefined" ? window.innerWidth <= 760 : false;

  const [isCompact, setIsCompact] = useState(getMatch);

  useEffect(() => {
    const handleResize = () => setIsCompact(getMatch());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isCompact;
}

function PersonIcon() {
  return (
    <svg fill="none" height="18" viewBox="0 0 24 24" width="18">
      <circle cx="12" cy="8" fill="#20263a" r="4" />
      <path
        d="M5 19c1.6-3.1 4.1-4.6 7-4.6s5.4 1.5 7 4.6"
        stroke="#20263a"
        strokeLinecap="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg fill="none" height="18" viewBox="0 0 24 24" width="18">
      <circle cx="6" cy="12" fill="#20263a" r="1.9" />
      <circle cx="12" cy="12" fill="#20263a" r="1.9" />
      <circle cx="18" cy="12" fill="#20263a" r="1.9" />
    </svg>
  );
}

const iconButtonStyle = {
  width: 44,
  height: 44,
  borderRadius: 16,
  border: "1px solid rgba(32, 38, 58, 0.06)",
  background: "#f6f8fc",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  boxShadow: "0 10px 20px rgba(32, 38, 58, 0.06)",
};

const menuPanelStyle = {
  position: "absolute",
  top: "calc(100% + 10px)",
  background: "#ffffff",
  borderRadius: 20,
  border: "1px solid rgba(32, 38, 58, 0.06)",
  boxShadow: "0 24px 48px rgba(32, 38, 58, 0.12)",
  padding: 14,
  zIndex: 40,
};

const menuPanelHeaderStyle = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  color: "#98a1b2",
  marginBottom: 10,
};

const menuListStyle = {
  display: "grid",
  gap: 8,
};

const menuItemStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  padding: "12px 14px",
  borderRadius: 16,
  border: "none",
  background: "#f7f8fb",
  color: "#4f596c",
  cursor: "pointer",
  fontWeight: 700,
  textAlign: "left",
};

const activeMenuItemStyle = {
  background: "#fff4ee",
  color: "#d96839",
};

const menuMetaTextStyle = {
  ...S.pageBody,
};
